import React, { useMemo, useEffect, useRef, useState } from "react";

const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const sanitizeImage = (image) => {
    if (!image) return null;
    const url = sanitize(image?.url || "");
    if (!url) return null;
    return { url, alt: sanitize(image?.alt || "") };
};

const TwoColumnFaqSection = ({ fields }) => {
    const contentRefs = useRef({});
    const rowRefs = useRef({});
    const [openIndex, setOpenIndex] = useState(0); // Track which FAQ is open (first one by default)
    const getStickyHeaderOffset = () => {
        const wrapper = document.querySelector(".header-wrapper");
        if (!wrapper) return 0;
        return wrapper.getBoundingClientRect().height || 0;
    };

    const keepAccordionVisibleOnMobile = (index) => {
        if (window.innerWidth > 1024) return;
        const row = rowRefs.current[index];
        if (!row) return;

        const headerOffset = getStickyHeaderOffset() + 12;
        const rowTop = row.getBoundingClientRect().top;
        const targetTop = window.scrollY + rowTop - headerOffset;

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: "smooth",
        });
    };


    const sanitizedData = useMemo(() => {
        if (!fields) return null;

        const sectionTitle = sanitize(fields.section_title || "");
        const sectionSubtitle = sanitize(fields.section_subtitle || "");

        const faqItems = Array.isArray(fields.faq_items)
            ? fields.faq_items
                .map((item) => {
                    if (!item) return null;

                    const faqIcon = sanitizeImage(item.faq_icon);
                    const faqTitle = sanitize(item.faq_title || "");
                    const faqSubtitle = sanitize(item.faq_subtitle || "");
                    const faqContent = sanitize(item.faq_content || "");

                    if (!faqTitle || !faqContent) return null;

                    return {
                        icon: faqIcon,
                        title: faqTitle,
                        subtitle: faqSubtitle,
                        content: faqContent,
                    };
                })
                .filter(Boolean)
            : [];

        const hasContent = sectionTitle || sectionSubtitle || faqItems.length > 0;
        if (!hasContent) return null;

        return {
            sectionTitle,
            sectionSubtitle,
            faqItems,
        };
    }, [fields]);

    // Calculate and set content height for smooth animation
    const setContentHeight = (index, isOpen) => {
        const contentElement = contentRefs.current[index];
        if (!contentElement) return;

        if (isOpen) {
            contentElement.style.height = "auto";
            const measuredHeight = Math.ceil(contentElement.scrollHeight) + 2;
            contentElement.style.height = `${measuredHeight}px`;
        } else {
            contentElement.style.height = "0px";
        }
    };

    // Handle FAQ toggle with one deterministic state.
    const handleFaqClick = (index) => {
        const shouldOpen = openIndex !== index;
        setOpenIndex((prev) => (prev === index ? null : index));

        if (shouldOpen) {
            setTimeout(() => {
                keepAccordionVisibleOnMobile(index);
            }, 120);
        }
    };

    useEffect(() => {
        const hash = window.location.hash;
        if (!hash) return;

        const id = hash.replace("#", "");

        const timer = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }, 500);

        return () => clearTimeout(timer);
    }, []);


    // Sync heights whenever open item changes
    useEffect(() => {
        if (!sanitizedData?.faqItems?.length) return;

        const frame = requestAnimationFrame(() => {
            sanitizedData.faqItems.forEach((_, index) => {
                setContentHeight(index, openIndex === index);
            });
        });

        const settleTimer = setTimeout(() => {
            if (openIndex !== null) {
                setContentHeight(openIndex, true);
            }
        }, 220);

        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(settleTimer);
        };
    }, [sanitizedData, openIndex]);

    // Keep open panel height correct when viewport changes.
    useEffect(() => {
        const handleResize = () => {
            if (openIndex === null) return;
            setContentHeight(openIndex, true);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [openIndex]);

    if (!sanitizedData) return null;

    const { sectionTitle, sectionSubtitle, faqItems } = sanitizedData;

    return (
        <section className="know-section">
            <div className="know__inner">
                {/* LEFT COLUMN */}
                {(sectionTitle || sectionSubtitle || faqItems.length > 0) && (
                    <div className="know__left aos-fade-up" data-stagger-index="0" >
                        {(sectionTitle || sectionSubtitle) && (
                            <div className="know__title-section aos-fade-up" data-stagger-index="0">
                                {sectionTitle && <h2 className="know__title" id="hours">{sectionTitle}</h2>}
                                {sectionSubtitle && <p className="know__subtitle">{sectionSubtitle}</p>}
                            </div>
                        )}

                        {faqItems.length > 0 && (
                            <div className="know__faq-list aos-fade-up" data-stagger-index="1" >
                                {faqItems.map((item, index) => {
                                    const isOpen = openIndex === index;
                                    return (
                                        <div
                                            key={`faq-${index}`}
                                            className="know_faq-row"
                                            data-stagger-index={index + 1}
                                            ref={(el) => (rowRefs.current[index] = el)}
                                        >
                                            <div className={`know_faq-item ${index === 0 ? "faq-item--lead" : ""} ${isOpen ? "know_faq-item--open" : ""}`}>
                                                <div
                                                    className="know_faq-item_summary"
                                                    onClick={() => handleFaqClick(index)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            handleFaqClick(index);
                                                        }
                                                    }}
                                                >
                                                    {item.icon && (
                                                        <span className="know_faq-icon">
                                                            <img
                                                                src={item.icon.url}
                                                                alt={item.icon.alt || item.title}
                                                            />
                                                        </span>
                                                    )}
                                                    <span className="know_faq-heading">
                                                        {item.title && (
                                                            <span className="know_faq-heading_title">
                                                                {item.title}
                                                            </span>
                                                        )}
                                                        {item.subtitle && (
                                                            <span className="know_faq-heading_subtitle">
                                                                {item.subtitle}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                                {item.content && (
                                                    <div
                                                        ref={(el) => (contentRefs.current[index] = el)}
                                                        className={`know_faq-item_content ${isOpen ? "know_faq-item_content--open" : ""}`}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.content,
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default TwoColumnFaqSection;

